import { z } from 'zod';

/**
 * Prepares the initial state of a form based on a Zod schema.
 *
 * For each field in the schema:
 * - If the field is optional and the provided default value is `null`, it sets it to `undefined`
 * - If the field is required, it sets the value from `defaultValues` if available, otherwise to an empty string (`''`)
 *
 * If the resulting object passes schema validation (`safeParse`), the parsed data is returned.
 * Otherwise, the fallback object is returned as-is.
 *
 * @template TFormSchema - A ZodObject schema defining the form structure
 * @param {TFormSchema} formSchema - The Zod schema for the form
 * @param {z.infer<TFormSchema>} [defaultValues] - Optional default values for the form fields
 * @returns {z.infer<TFormSchema>} - The initial form state, validated if possible
 */
export function getInitialFormState<TFormSchema extends z.ZodObject<z.ZodRawShape>>(
  formSchema: TFormSchema,
  defaultValues?: Partial<z.infer<TFormSchema>>
): z.infer<TFormSchema> {
  const emptyFields = Object.keys(formSchema.shape).reduce((acc, key) => {
    const fieldKey = key as keyof typeof formSchema.shape as string;
    const fieldSchema = formSchema.shape[fieldKey];
    if (fieldSchema instanceof z.ZodOptional) {
      return {
        ...acc,
        [key]: defaultValues?.[fieldKey] === null ? undefined : defaultValues?.[fieldKey],
      };
    }
    return { ...acc, [key]: defaultValues?.[fieldKey] ?? '' };
  }, {});

  const result = formSchema.safeParse(emptyFields);
  return result.success ? result.data : (emptyFields as TFormSchema);
}
